import React from 'react';
import { useNavigate } from 'react-router-dom';

const APP_TYPES = [
  {
    id: 'moebius-comparison',
    title: 'emew vs Moebius',
    subtitle: '(Full OpEx Comparison)',
    description: 'Full operating cost comparison of emew Rapid Silver + Copper Plate versus Moebius electrorefining — reagents, power, labour, silver inventory, copper revenue and CAPEX payback across 100–1,000 kg Ag/d.',
    icon: '\u2696\ufe0f',
    accentColor: '#38D430',
    available: true,
    route: '/assessment/moebius-comparison/new',
  },
  {
    id: 'moebius-bleed',
    title: 'Moebius Bleed',
    subtitle: '(Precipitation → emew EW)',
    description: 'Replace conventional silver precipitation from Moebius electrolyte bleed with emew electrowinning — recovering higher-purity silver with lower reagent consumption, no filter cake waste, and a direct path to saleable product.',
    icon: '🧪',
    accentColor: '#805AD5',
    available: true,
    route: '/assessment/moebius-bleed/new',
  },
  {
    id: 'moebius-replacement',
    title: 'Moebius Replacement',
    subtitle: null,
    description: 'Full techno-economic comparison of replacing a Moebius electrorefinery with emew electrowinning.',
    icon: '⚡',
    accentColor: '#3E68B2',
    available: true,
    route: '/assessment/moebius-replacement/new',
  },
  {
    id: 'copper-wastewater',
    title: 'Copper Wastewater',
    subtitle: null,
    description: 'Assess the value of recovering copper from industrial wastewater or process rinse streams.',
    icon: '💧',
    accentColor: '#2B8A6D',
    available: false,
    route: null,
  },
  {
    id: 'copper-refinery-bleed',
    title: 'Copper Refinery Bleed',
    subtitle: null,
    description: 'Evaluate precious metal recovery from copper electrorefinery bleed electrolyte streams.',
    icon: '🏭',
    accentColor: '#D4870A',
    available: false,
    route: null,
  },
  {
    id: 'ewaste-refining',
    title: 'E-waste Refining',
    subtitle: '(Cu + PM)',
    description: 'Value assessment for copper and precious metal recovery from electronic scrap leach solutions.',
    icon: '♻️',
    accentColor: '#2B7A2B',
    available: false,
    route: null,
  },
];

export default function AssessmentSelector() {
  const navigate = useNavigate();

  return (
    <div style={{ padding: '1.5rem 0' }}>

      {/* Hero */}
      <div className="card" style={{ padding: '2rem 2.5rem', marginBottom: '2rem' }}>
        <h1 style={{ marginBottom: '0.375rem' }}>
          <span style={{ color: 'var(--color-green)' }}>e</span>mew AIVA
        </h1>
        <p style={{ color: 'var(--color-text-secondary)', maxWidth: 520 }}>
          Select the application type below to launch the corresponding value assessment tool.
        </p>
      </div>

      {/* Section label */}
      <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
        Select Application Type
      </div>

      {/* Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
        {APP_TYPES.map((app) => (
          <div
            key={app.id}
            className="card"
            onClick={() => app.available && navigate(app.route)}
            style={{
              padding: '1.5rem',
              cursor: app.available ? 'pointer' : 'default',
              opacity: app.available ? 1 : 0.62,
              border: `1px solid ${app.available ? app.accentColor + '55' : 'var(--color-border)'}`,
              transition: 'box-shadow 0.15s ease, transform 0.15s ease',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={e => {
              if (app.available) {
                e.currentTarget.style.boxShadow = '0 4px 18px rgba(0,0,0,0.13)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = '';
              e.currentTarget.style.transform = '';
            }}
          >
            {/* Top accent bar */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 3,
              background: app.accentColor,
              opacity: app.available ? 1 : 0.35,
            }} />

            {/* Icon + badge row */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: '0.25rem', marginBottom: '0.875rem' }}>
              <span style={{ fontSize: '1.875rem', lineHeight: 1 }}>{app.icon}</span>
              {app.available ? (
                <span style={{
                  fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                  color: '#fff', background: 'var(--color-green)', borderRadius: 4, padding: '2px 8px',
                }}>
                  Active
                </span>
              ) : (
                <span style={{
                  fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
                  color: 'var(--color-text-muted)', background: '#F0F1F3', border: '1px solid var(--color-border)',
                  borderRadius: 4, padding: '2px 8px',
                }}>
                  Coming Soon
                </span>
              )}
            </div>

            {/* Title */}
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                {app.title}
              </span>
              {app.subtitle && (
                <span style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginLeft: '0.375rem' }}>
                  {app.subtitle}
                </span>
              )}
            </div>

            {/* Description */}
            <p style={{
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.55,
              marginBottom: app.available ? '1.25rem' : 0,
            }}>
              {app.description}
            </p>

            {/* CTA — only for active tools */}
            {app.available && (
              <button
                className="btn btn-primary"
                style={{ width: '100%', fontSize: '0.875rem' }}
                onClick={e => { e.stopPropagation(); navigate(app.route); }}
              >
                Start Assessment →
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
